import {
  ChangeDetectionStrategy,
  Component,
  inject,
  ElementRef,
  ViewChild,
  AfterViewInit,
  OnDestroy,
  ChangeDetectorRef,
  effect,
} from '@angular/core';
import { RouterLink, Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';

import { UserPrefsService } from '../../core/services/user-prefs.service';
import { AppEntry } from '../../core/models/app-entry.model';
import { RailItemComponent } from './rail-item/rail-item.component';
import { FormsServiceService } from '../../api/services/forms-service.service';
import { NotificationService } from '../../core/services/notification.service';

@Component({
  selector: 'ec-app-rail',
  imports: [RouterLink, RailItemComponent],
  templateUrl: './app-rail.component.html',
  styleUrl: './app-rail.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppRailComponent implements AfterViewInit, OnDestroy {
  protected readonly prefs = inject(UserPrefsService);
  protected readonly notifs = inject(NotificationService, { optional: true });
  @ViewChild('menuViewport', { static: true })
  private readonly menuViewport!: ElementRef<HTMLElement>;

  protected canScrollDown = false;
  protected canScrollUp = false;

  private readonly cd = inject(ChangeDetectorRef);
  private readonly router = inject(Router);
  private _routerSub?: Subscription;
  private _onScroll?: () => void;
  private _ro?: ResizeObserver;
  constructor(private formService: FormsServiceService) {
    effect(() => {
      this.prefs.railApps();
      this.prefs.personalApps();
      this.cd.markForCheck();
    });
  }
  get builtInApps() {
    return this.prefs.railApps();
  }
  get personalApps() {
    return this.prefs.personalApps();
  }

  scrollDown(): void {
    const el = this.menuViewport.nativeElement;
    el.scrollBy({ top: Math.ceil(el.clientHeight * 0.75), behavior: 'smooth' });
  }

  scrollUp(): void {
    const el = this.menuViewport.nativeElement;
    el.scrollBy({
      top: -Math.ceil(el.clientHeight * 0.75),
      behavior: 'smooth',
    });
  }

  ngAfterViewInit(): void {
    this.syncScrollState();
    this._onScroll = () => this.syncScrollState();
    this.menuViewport.nativeElement.addEventListener('scroll', this._onScroll);
    this._ro = new ResizeObserver(() => this.syncScrollState());
    this._ro.observe(this.menuViewport.nativeElement);
    this._routerSub = this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe(() => this.cd.markForCheck());
  }

  ngOnDestroy(): void {
    this._ro?.disconnect();
    if (this._onScroll)
      this.menuViewport.nativeElement.removeEventListener(
        'scroll',
        this._onScroll,
      );
    this._routerSub?.unsubscribe();
  }

  get unreadNotifs(): number {
    return this.notifs?.unreadCount() ?? 0;
  }

  isIntegrationActive(): boolean {
    return this.router.url.includes('/integration-marketplace');
  }

  isNotificationActive(): boolean {
    return (
      this.router.url.includes('/notifications') ||
      this.router.url.includes('/notification-preferences')
    );
  }

  isSettingsActive(): boolean {
    const url = this.router.url;
    if (this.isNotificationActive()) {
      return false;
    }
    return (
      url.includes('/onboarding') ||
      url.includes('/organization-setting') ||
      url.includes('/employees') ||
      url.includes('/access-management') ||
      url.includes('/my-profile') ||
      url.includes('/general-settings') ||
      url.includes('/appearance') ||
      url.includes('/branding') ||
      url.includes('/reorder') ||
      url.includes('/personal') ||
      url.includes('/settings')
    );
  }

  openSettings(event: Event): void {
    event.preventDefault();
    this.router.navigate(['/eciso/organization-setting']);
    // this.loadForm('68511d54c23e7a0f7ae563ff');
  }

  loadForm(formId: any) {
    this.formService.loadFormData(formId).subscribe({
      next: (data) => {
        let formRecordID = data?.data?.formCollection?.recordId;
        if (formRecordID) {
          const currentUrl = this.router.url;
          const targetUrl = `/eciso/onboarding/${formId}/${formRecordID}`;

          if (!currentUrl.startsWith(targetUrl)) {
            this.router.navigate(['/eciso/onboarding', formId, formRecordID]);
          }
        }
      },
      error: (err) => console.error('Error loading form JSON:', err),
    });
  }

  private syncScrollState(): void {
    const el = this.menuViewport.nativeElement;
    const newDown = el.scrollTop + el.clientHeight < el.scrollHeight - 4;
    const newUp = el.scrollTop > 4;
    if (this.canScrollDown !== newDown || this.canScrollUp !== newUp) {
      this.canScrollDown = newDown;
      this.canScrollUp = newUp;
      this.cd.markForCheck();
    }
  }
}
